import React, { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useDispatch } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"

import { Card, Grid } from "@mui/material"
import { Button, Skeleton, Tag, Tooltip, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import moment from "moment"

import { KonanAvatarComponent } from "../../../components/core/KonanAvatar"
import { KonanLogo } from "../../../components/icons/KonanLogo"
import { ModelType } from "../../../components/status/ModelType"
import { KonanAPI } from "../../../services/KonanAPI"
import type { Model } from "../../../types/generated/api/Model"
import type { ModelListCreate } from "../../../types/generated/api/ModelListCreate"
import { RetrainingJob } from "../../../types/generated/api/RetrainingJob"
import type { TrainingDataUpload } from "../../../types/generated/api/TrainingDataUpload"
import { TrainingJob } from "../../../types/generated/api/TrainingJob"
import { uniqueJobName } from "../../../utils/deploymentDetailsHelpers"
import { KonanTimeHelper } from "../../../utils/genericHelpers"
import { setCurrentModel } from "../../Models"
import { AbortTrainingJobDialog } from "./AbortTrainingJobDialog"
import { RetrainingLogsView } from "./RetrainingLogs"

import styles from "../TrainingJobs.module.scss"

type Props = {
  predictions: string
  parentModel: string
  startedAt: string
  firstPredictionDate: string
  lastPredictionDate: string
  createdModelName: string
  endedAt: string
  trainingDataId: string
  liveModel: Model | ModelListCreate | undefined
  cardType: string
  uuid: string
  trainingOrRetraining: string
  isLoading?: boolean

  status: RetrainingJob.status

  createdAt: string
  createdModelStatus?: string

  projectType?: string
  /** Used to guarantee that avatars are unique per user. */
  userName?: string

  duration?: string
  dataPoints?: string
  evaluationTests?: { label: string; data: string }[]

  onCancel?: () => void
  viewLogs?: (uuid: string) => void
  viewStats?: () => void
}

export function PendingFailedTrainingJobCard(props: Readonly<Props>): React.ReactElement {
  const { t } = useTranslation("TrainingJob")
  const {
    isLoading,
    status,
    startedAt,
    createdAt,
    parentModel,
    userName,
    firstPredictionDate,
    lastPredictionDate,
    dataPoints,
    trainingDataId,
    endedAt,
    projectType,
    uuid,
    duration,
    trainingOrRetraining,
    cardType,
    liveModel,
  } = props

  const { id: projectId } = useParams<{ id: string }>()

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const konanTime = new KonanTimeHelper()

  const isJobPendingAndAutoMLTraining = trainingOrRetraining === "training-automl" && cardType === "pending"
  const isStatusPendingOrRunning = status === TrainingJob.status.PENDING || TrainingJob.status.RUNNING

  const [openRetrainingLogs, setOpenRetrainingLogs] = useState(false)
  const [AbortDialogOpen, setAbortDialogOpen] = useState(false)

  // fetch model data
  const { isLoading: isParentModelDataLoading, data: parentModelData } = useQuery<AxiosResponse<Model>, AxiosError>(
    ["model", parentModel],
    () => KonanAPI.fetchModel(parentModel),
    { enabled: !!parentModel },
  )

  const { data: trainingData, isLoading: isTrainingDataLoading } = useQuery<
    AxiosResponse<TrainingDataUpload>,
    AxiosError
  >(
    ["training-data", projectId, trainingDataId],
    () => KonanAPI.fetchSingleTrainingData(projectId as string, trainingDataId),
    { enabled: projectId != null && trainingDataId != null },
  )

  const handleLogsClose = (): void => {
    setOpenRetrainingLogs(false)
  }

  return (
    <Fragment>
      <RetrainingLogsView
        open={openRetrainingLogs}
        onClose={handleLogsClose}
        projectId={projectId as string}
        retrainingUUID={uuid}
        jobStatus={status}
        trainingJobType={trainingOrRetraining}
        trainingName={uniqueJobName(uuid, createdAt, trainingOrRetraining)}
        isAutoMl={Boolean(trainingOrRetraining === "training-automl")}
      />
      <AbortTrainingJobDialog
        projectId={projectId as string}
        trainingUUID={uuid}
        open={AbortDialogOpen}
        onClose={() => setAbortDialogOpen(false)}
        trainingOrRetraining={trainingOrRetraining}
        liveModel={liveModel}
      />
      <Card className="card-box-shadow">
        <Grid container direction="column" className={styles.headerContainer}>
          <Grid item xs={12} container justifyContent="space-between" alignItems="flex-start">
            <Grid item xs={12} container direction="row">
              <Grid item>
                <Tag size="small" color={status === "FAILED" ? "red" : status === "CANCELLED" ? "yellow" : "blue"}>
                  {status?.toLowerCase()}
                </Tag>
              </Grid>
              <Grid item direction="column" justifyContent="center" alignItems="flex-end">
                <Typography variant="span-regular" className={styles.durationText} noWrap>
                  {cardType === "failed" && !isLoading && duration
                    ? `${t("labels.lasted")} ${konanTime.timeParser(duration)}`
                    : ""}
                  {cardType === "pending" && !isLoading && status === "PENDING" && t("labels.waitingInQueue")}
                  {cardType === "failed" &&
                    !isLoading &&
                    status === "RUNNING" &&
                    `${t("labels.running")} ${konanTime.formatTimeMinsAndSec(moment().diff(startedAt, "seconds"))}`}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h3-bold" noWrap className={styles.headerName}>
              {uniqueJobName(uuid, createdAt, trainingOrRetraining)}
            </Typography>
          </Grid>

          <Grid container justifyContent="space-between" className={styles.avatarSpacing}>
            <Grid
              container
              justifyContent="space-between"
              item
              xs={isJobPendingAndAutoMLTraining ? 12 : isStatusPendingOrRunning ? 9 : 8}
              alignItems="flex-start"
              className={styles.avatarContainer}
            >
              <KonanAvatarComponent createdBy={userName ?? ""} title={userName} createdAt={createdAt} />
            </Grid>

            <Grid
              item
              direction="row"
              xs={isStatusPendingOrRunning ? 3 : 4}
              container
              className={styles.buttonsContainer}
            >
              <Grid item>
                {cardType === "failed" ? (
                  <Button onClick={() => setOpenRetrainingLogs(true)} size="small" variant="secondary">
                    {t("cards.pending.viewLogs")}
                  </Button>
                ) : ["PENDING", "RUNNING"].includes(status) && trainingOrRetraining !== "training-automl" ? (
                  <Button
                    variant="dangerous"
                    style={{ marginRight: "3px" }}
                    onClick={() => setAbortDialogOpen(true)}
                    size="small"
                  >
                    {t("cards.pending.abort")}
                  </Button>
                ) : (
                  ""
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid container direction="column" className={styles.pendingBodyContainer}>
          <Grid container item direction="row" alignItems="flex-end">
            <Grid container item direction="row">
              <Grid item xs={12} sm={9}>
                <Grid container direction="row" justifyContent="flex-start" alignItems="flex-end">
                  <Typography
                    variant="label-bold"
                    tooltip={
                      trainingOrRetraining === "training-automl" || trainingOrRetraining === "training-prebuilt"
                        ? t("cards.successful.trainedModelDescription")
                        : t("cards.successful.originalVersionDescription")
                    }
                    tooltipVerticalAlign="middle"
                  >
                    {t("cards.successful.sourceModel")}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid>
            <Grid container className={styles.sourceModelSection}>
              {trainingOrRetraining === "training-automl" || trainingOrRetraining === "training-prebuilt" ? (
                <Fragment>
                  <Grid item marginBottom={1}>
                    <Typography variant="span-bold">{projectType}</Typography>
                  </Grid>
                  <Grid item>
                    <KonanLogo logoWidth={115} />
                  </Grid>
                </Fragment>
              ) : (
                <Grid item container direction="column" className={styles.pendingSourceModel}>
                  {!isParentModelDataLoading && parentModelData?.data && (
                    <Fragment>
                      <Grid item>
                        <ModelType modelState={parentModelData?.data?.state} size="small" />
                      </Grid>
                      <Grid
                        spacing={1}
                        item
                        onClick={() => {
                          navigate(`/projects/${projectId}/decision-engines`)
                          dispatch(setCurrentModel(parentModelData?.data))
                        }}
                        sx={{ width: "100%" }}
                      >
                        <Typography variant="p" noWrap className={styles.linkText} color="important" variantColor={2}>
                          {parentModelData?.data?.name}
                        </Typography>
                      </Grid>

                      <Grid className={`${styles.dateContainer} ${styles.pendingDataWrapper}`} item>
                        <Tooltip
                          display="auto"
                          title={moment(
                            parentModelData?.data?.created_at ? parentModelData?.data?.created_at : "",
                          ).format("DD/MM/YYYY, h:mm A")}
                          placement="right"
                        >
                          <Typography variant="label-regular">
                            {moment(
                              parentModelData?.data?.created_at ? parentModelData?.data?.created_at : "",
                            ).fromNow()}
                          </Typography>
                        </Tooltip>
                      </Grid>
                    </Fragment>
                  )}
                  {isParentModelDataLoading && (
                    <Grid
                      container
                      item
                      xs={12}
                      direction="column"
                      justifyContent="flex-start"
                      alignItems="flex-start"
                      flexWrap="nowrap"
                    >
                      <Grid container direction="column" className={"generatedModelCard"}>
                        <Grid item>
                          <Skeleton variant="rectangular" width={"40%"} height={10} />
                        </Grid>
                        <Grid item>
                          <Skeleton variant="rectangular" width={"70%"} height={10} />
                        </Grid>
                        <Grid item>
                          <Skeleton variant="rectangular" width={"40%"} height={10} />
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              )}
            </Grid>
          </Grid>
          {cardType === "failed" && (
            <Grid container item direction="row" className={styles.twoBoxesContainer}>
              <div className={styles.testWidthLeft}>
                <Typography
                  variant="label-bold"
                  className={styles.labelColor}
                  tooltipVerticalAlign="middle"
                  tooltip={t("tooltips.startDatetime", {
                    type:
                      trainingOrRetraining === "training-automl" || trainingOrRetraining === "training-prebuilt"
                        ? t("cards.successful.type.training")
                        : t("cards.successful.type.retraining"),
                  })}
                >
                  {t("labels.started")}
                </Typography>
                <Grid item className={styles.idbox} sx={{ width: "100px" }}>
                  <Typography variant="p" noWrap className={styles.textIdBox}>
                    {startedAt ? moment(startedAt).format("DD/MM/YYYY, h:mm:ss A") : t("common.na")}
                  </Typography>
                </Grid>
              </div>
              <div className={styles.testWidthRight}>
                <Typography
                  variant="label-bold"
                  className={styles.labelColor}
                  tooltip={t("tooltips.endDatetime", {
                    type:
                      trainingOrRetraining === "training-automl" || trainingOrRetraining === "training-prebuilt"
                        ? t("cards.successful.type.training")
                        : t("cards.successful.type.retraining"),
                  })}
                  tooltipVerticalAlign="middle"
                >
                  {t("labels.ended")}
                </Typography>
                <Grid item className={styles.idbox} sx={{ width: "100px" }}>
                  <Typography variant="p" noWrap className={styles.textIdBox}>
                    {endedAt ? moment(endedAt).format("DD/MM/YYYY, h:mm:ss A") : t("common.na")}
                  </Typography>
                </Grid>
              </div>
            </Grid>
          )}

          <Grid container item direction="row" className={styles.twoBoxesContainer}>
            <div className={styles.testWidthLeft}>
              {trainingOrRetraining === "retraining" ? (
                <Fragment>
                  <Typography
                    variant="label-bold"
                    className={styles.labelColor}
                    tooltip={t("tooltips.servingDataRange")}
                    tooltipVerticalAlign="middle"
                  >
                    {t("labels.servingData")}
                  </Typography>
                  <Grid container className={styles.idbox}>
                    {firstPredictionDate && lastPredictionDate ? (
                      <Typography variant="p" noWrap className={styles.textIdBox}>
                        {moment(firstPredictionDate).format("DD/MM/YYYY")} -{" "}
                        {moment(lastPredictionDate).format("DD/MM/YYYY")}
                      </Typography>
                    ) : (
                      <Typography variant="p">{t("common.na")}</Typography>
                    )}
                  </Grid>
                </Fragment>
              ) : (
                <Fragment>
                  <Typography
                    variant="label-bold"
                    className={styles.labelColor}
                    tooltip={t("tooltips.trainingDataUsed")}
                    tooltipVerticalAlign="middle"
                  >
                    {t("labels.trainingData")}
                  </Typography>
                  <Grid container className={styles.idbox}>
                    <Grid item xs={12}>
                      {isTrainingDataLoading && (
                        <Typography variant="p">
                          <Skeleton variant="rectangular" width={"100%"} height={10} />
                        </Typography>
                      )}
                    </Grid>
                    {!isTrainingDataLoading && trainingData?.data?.name && (
                      <Typography variant="p" noWrap className={styles.textIdBox}>
                        {trainingData.data?.name}
                      </Typography>
                    )}
                    {!isTrainingDataLoading && trainingData?.data?.uuid && !trainingData.data?.name && (
                      <Typography variant="p" noWrap className={styles.textIdBox}>
                        data.csv
                      </Typography>
                    )}
                    {!isTrainingDataLoading && !trainingData?.data?.uuid && !trainingData?.data?.name && (
                      <Typography variant="p" noWrap className={styles.textIdBox}>
                        {t("common.na")}
                      </Typography>
                    )}
                  </Grid>
                </Fragment>
              )}
            </div>
            <div className={styles.testWidthRight}>
              <Typography
                variant="label-bold"
                className={styles.labelColor}
                tooltip={t("tooltips.dataSplitPercentage")}
                tooltipVerticalAlign="middle"
              >
                {t("labels.dataSplit")}
              </Typography>
              <Grid container className={styles.idbox}>
                {status === RetrainingJob.status.SUCCEEDED && dataPoints && !isLoading ? (
                  <Typography variant="p" noWrap className={styles.textIdBox}>
                    {dataPoints}
                  </Typography>
                ) : isLoading ? (
                  <div>
                    <Skeleton variant="rectangular" width="100%" height={20} />
                  </div>
                ) : (
                  <Typography variant="p" noWrap className={styles.textIdBox}>
                    {t("common.na")}
                  </Typography>
                )}
              </Grid>
            </div>
          </Grid>
        </Grid>
      </Card>
    </Fragment>
  )
}
